import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  AppBar,
  Toolbar,
  Stack,
  Divider,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  AdminPanelSettings as AdminIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { fetchSchoolByDomain, getSchoolImageUrl, getSchoolLogoUrl, SchoolPublicData } from '../services/publicSchoolService';
import { colors } from '../colors';

interface Props {
  domainName: string;
}

const SchoolLandingPage: React.FC<Props> = ({ domainName }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [school, setSchool] = useState<SchoolPublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loginAnchor, setLoginAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchSchoolByDomain(domainName)
      .then(setSchool)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [domainName]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: '#f0f4f8' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !school) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: '#f0f4f8', gap: 2 }}>
        <SchoolIcon sx={{ fontSize: 64, color: '#9e9e9e' }} />
        <Typography variant="h5" color="text.secondary">School not found</Typography>
        <Typography variant="body2" color="text.secondary">
          No school is registered with this domain name.
        </Typography>
      </Box>
    );
  }

  const heroImageUrl = school.schoolImage ? getSchoolImageUrl(school.schoolImage) : null;
  const logoUrl = school.schoolLogo ? getSchoolLogoUrl(school.schoolLogo) : null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>

      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="School Logo"
                sx={{ height: 44, width: 44, objectFit: 'contain', borderRadius: 1 }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <SchoolIcon sx={{ color: '#1976d2', fontSize: 36 }} />
            )}
            <Typography
              variant="h6"
              sx={{
                color: '#1a237e',
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                lineHeight: 1.2,
                maxWidth: { xs: 200, sm: 400, md: 'none' },
              }}
            >
              {school.nameofSchool}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl
              size="small"
              sx={{
                minWidth: 120,
                '& .MuiSelect-select': { fontWeight: 500, py: 1 },
              }}
            >
              <InputLabel sx={{ color: colors.gray }}>{t('language')}</InputLabel>
              <Select
                value={i18n.language}
                onChange={(e) => {
                  const newLang = e.target.value;
                  i18n.changeLanguage(newLang);
                  try {
                    localStorage.setItem('selectedLanguage', newLang);
                    localStorage.setItem('i18nextLng', newLang);
                  } catch (error) {}
                }}
                label={t('language')}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      border: `1px solid ${colors.primaryLight}`,
                      mt: 1,
                      '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(48, 146, 227, 0.08)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(48, 146, 227, 0.15)',
                          color: colors.primary,
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(48, 146, 227, 0.2)' },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>

          <Button
            variant="contained"
            onClick={(e) => setLoginAnchor(e.currentTarget)}
            sx={{
              bgcolor: colors.primary,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover': {
                bgcolor: colors.secondary,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(1, 104, 234, 0.4)',
                '&::before': {
                  left: '100%',
                },
              },
              '&:active': {
                transform: 'translateY(0) scale(0.98)',
              },
            }}
          >
            {t('landing_login')}
          </Button>
          <Menu
            anchorEl={loginAnchor}
            open={Boolean(loginAnchor)}
            onClose={() => setLoginAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: 3,
                border: `1px solid ${colors.primaryLight}`,
                mt: 1.5,
                minWidth: 240,
                boxShadow: '0 8px 24px rgba(48, 146, 227, 0.2)',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => { setLoginAnchor(null); navigate('/login?type=student'); }}
              sx={{
                py: 2,
                px: 2.5,
                gap: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(48, 146, 227, 0.12)',
                  transform: 'translateX(6px)',
                  '& .menu-icon': { transform: 'scale(1.15) rotate(5deg)', color: colors.primary },
                  '& .menu-text': { fontWeight: 600, color: colors.primary },
                },
              }}
            >
              <PersonIcon className="menu-icon" sx={{ color: colors.primary, fontSize: 24, transition: 'all 0.3s ease' }} />
              <Typography className="menu-text" variant="body1" sx={{ fontWeight: 500, transition: 'all 0.3s ease', color: colors.darkGray }}>
                {t('landing_student_login') || 'Student/Parent Login'}
              </Typography>
            </MenuItem>
            <Box sx={{ height: '1px', background: `linear-gradient(90deg, transparent, ${colors.primaryLight}, transparent)`, mx: 1 }} />
            <MenuItem
              onClick={() => { setLoginAnchor(null); navigate('/login?type=staff'); }}
              sx={{
                py: 2,
                px: 2.5,
                gap: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(48, 146, 227, 0.12)',
                  transform: 'translateX(6px)',
                  '& .menu-icon': { transform: 'scale(1.15) rotate(-5deg)', color: colors.secondary },
                  '& .menu-text': { fontWeight: 600, color: colors.secondary },
                },
              }}
            >
              <AdminIcon className="menu-icon" sx={{ color: colors.secondary, fontSize: 24, transition: 'all 0.3s ease' }} />
              <Typography className="menu-text" variant="body1" sx={{ fontWeight: 500, transition: 'all 0.3s ease', color: colors.darkGray }}>
                {t('landing_staff_login') || 'Staff / Admin Login'}
              </Typography>
            </MenuItem>
          </Menu>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          mt: '64px',
          position: 'relative',
          height: { xs: 280, sm: 380, md: 480 },
          overflow: 'hidden',
          background: heroImageUrl ? 'transparent' : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
        }}
      >
        {heroImageUrl && (
          <Box
            component="img"
            src={heroImageUrl}
            alt="School"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.65) 100%)',
          }}
        />
        {/* Hero Text */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            p: { xs: 3, md: 6 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.8rem' },
              lineHeight: 1.2,
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              maxWidth: 800,
            }}
          >
            {school.nameofSchool}
          </Typography>
          <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
            {school.district && (
              <Chip
                icon={<LocationIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={`${school.district}, ${school.state}`}
                size="small"
                sx={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 500, backdropFilter: 'blur(4px)' }}
              />
            )}
            {school.schoolCode && (
              <Chip
                label={`Code: ${school.schoolCode}`}
                size="small"
                sx={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 500, backdropFilter: 'blur(4px)' }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      {/* Info Cards */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto', width: '100%' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            mt: 2,
          }}
        >
          {/* School Details Card */}
          <Box
            sx={{
              background: '#fff',
              borderRadius: 3,
              p: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
              <Box sx={{ p: 1, borderRadius: 2, background: '#e3f2fd' }}>
                <SchoolIcon sx={{ color: '#1976d2', fontSize: 22 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#1a237e">
                School Information
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              <InfoRow label="School Code" value={school.schoolCode} />
              <InfoRow label="DISE Code" value={school.diseCode} />
              <InfoRow label="District" value={school.district} />
              <InfoRow label="State" value={school.state} />
              <InfoRow label="PIN Code" value={school.pinNo} />
            </Stack>
          </Box>

          {/* Principal Details Card */}
          <Box
            sx={{
              background: '#fff',
              borderRadius: 3,
              p: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
              <Box sx={{ p: 1, borderRadius: 2, background: '#e8f5e9' }}>
                <PersonIcon sx={{ color: '#388e3c', fontSize: 22 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#1a237e">
                Principal
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              <InfoRow label="Name" value={school.schoolPrincipalName} />
              <Stack direction="row" alignItems="center" spacing={1}>
                <PhoneIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>Mobile</Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  component="a"
                  href={`tel:${school.principalMobile}`}
                  sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {school.principalMobile}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>Email</Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  component="a"
                  href={`mailto:${school.principalEmail}`}
                  sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, wordBreak: 'break-all' }}
                >
                  {school.principalEmail}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Application Form Section */}
        <Box
          sx={{
            mt: 3,
            background: '#fff',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <SchoolIcon
            sx={{
              fontSize: { xs: 80, md: 100 },
              color: colors.primary,
              opacity: 0.25,
              animation: 'float 6s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                '50%': { transform: 'translateY(-14px) rotate(5deg)' },
              },
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: colors.darkGray,
              lineHeight: 1.3,
              maxWidth: 480,
            }}
          >
            Click here to begin your application form.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/register')}
            sx={{
              position: 'relative',
              overflow: 'visible',
              background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 50%, #ff6b5a 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 800,
              px: { xs: 5, md: 6 },
              py: { xs: 2, md: 2 },
              borderRadius: 999,
              minWidth: { xs: '100%', sm: 360 },
              boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5)',
              transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
              animation: 'pulseGlow 2s ease-in-out infinite 1s',
              letterSpacing: 0.8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              '@keyframes pulseGlow': {
                '0%, 100%': { boxShadow: '0 12px 35px rgba(255, 65, 108, 0.5), 0 0 0 0 rgba(255, 65, 108, 0.7)' },
                '50%': { boxShadow: '0 12px 35px rgba(255, 65, 108, 0.6), 0 0 0 8px rgba(255, 65, 108, 0)' },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: 999,
                background: 'linear-gradient(135deg, #ff416c, #ff4b2b, #ff6b5a)',
                opacity: 0.3,
                filter: 'blur(8px)',
                zIndex: -1,
                transition: 'opacity 0.4s ease',
              },
              '&:hover': {
                transform: 'translateY(-6px) scale(1.05)',
                boxShadow: '0 20px 50px rgba(255, 65, 108, 0.7)',
                background: 'linear-gradient(135deg, #ff4d6d 0%, #ff3333 50%, #ff7a6b 100%)',
                '&::before': { opacity: 0.5 },
              },
              '&:active': {
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(255, 65, 108, 0.6)',
              },
              '&:hover .cta-arrow': { transform: 'translateX(8px) scale(1.1)' },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: `'Lato', 'Roboto', 'Segoe UI', sans-serif`,
                  fontWeight: 800,
                  letterSpacing: 1,
                  fontSize: { xs: '1.1rem', md: '1.2rem' },
                  textTransform: 'uppercase',
                  textShadow: '0 2px 4px rgba(0,0,0,0.25)',
                }}
              >
                School Selection Portal
              </Typography>
              <ArrowForwardIcon
                className="cta-arrow"
                sx={{ fontSize: { xs: 22, md: 26 }, transition: 'transform 0.3s ease', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
              />
            </Box>
          </Button>
        </Box>

        {/* Login CTA */}
        <Box
          sx={{
            mt: 3,
            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" fontWeight={700} color="#fff" mb={1}>
            Welcome to {school.nameofSchool}
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.8)" mb={3}>
            Access your school portal to manage academics, attendance, results and more.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login?type=student')}
              sx={{
                background: '#fff',
                color: '#1565c0',
                fontWeight: 700,
                borderRadius: 3,
                px: 4,
                textTransform: 'none',
                '&:hover': { background: '#e3f2fd' },
              }}
            >
              {t('landing_student_login') || 'Student/Parent Login'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login?type=staff')}
              sx={{
                borderColor: '#fff',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 3,
                px: 4,
                textTransform: 'none',
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
              }}
            >
              Staff / Admin Login
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 2, textAlign: 'center', borderTop: '1px solid #e0e0e0', background: '#fff' }}>
        <Typography variant="caption" color="text.secondary">
          Powered by iSchoolNext &copy; {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={600} color="text.primary">{value || '—'}</Typography>
  </Stack>
);

export default SchoolLandingPage;
